/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unused-prop-types */

import React from "react";
import "./TawkPill.css";
import { useNavigate } from "react-router-dom";
import {
  TawkColorsProvider,
  tawksColorsWithCharacteristicHue,
} from "../../Infrastucture/TawkitColorsProvider";
import {
  GenericImageConfig,
  GenericImageComponent,
} from "../GenericImageComponent/GenericImageComponent";

import { assertUnreachable } from "../../Infrastucture/MissingStd";

export type TawkPillSize = "small" | "normal" | "big";

export interface TawkPillConfig {
  tawkTitle: string;
  authorProfilePicConfig: GenericImageConfig;
  authorUserHandle: string;
  colorsProvider: TawkColorsProvider;
  durationDescription: string;
  isClip: boolean;
  size: TawkPillSize;
}

export const demoProps1: TawkPillConfig = {
  tawkTitle:
    "Hello, there. This is a test. And the test can sometimes go on and on and on and on.",
  authorProfilePicConfig: {
    kind: "CloudinaryImageConfig",
    publicId: "ProfilePictures/default",
  },
  authorUserHandle: "atai",
  colorsProvider: tawksColorsWithCharacteristicHue(0.5),
  durationDescription: "0:42",
  isClip: false,
  size: "normal",
};

export const demoProps2: TawkPillConfig = {
  tawkTitle: "Hello, tawk again",
  authorProfilePicConfig: {
    kind: "CloudinaryImageConfig",
    publicId: "ProfilePictures/srqxxwbc8sli6abz5nsi",
  },
  authorUserHandle: "atai",
  colorsProvider: tawksColorsWithCharacteristicHue(0.8),
  durationDescription: "1:48",
  isClip: true,
  size: "small",
};

function conditionalScissorIcon(props: TawkPillConfig): JSX.Element | null {
  const scissorIcon = (
    <div className="tawk-pill-scissor-icon">
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.7584 11.3704C12.3073 11.6698 12.9467 11.7491 13.5591 11.5941C14.1714 11.4394 14.683 11.0684 15 10.5502L9.40179 7.50001L7.02782 6.20667L5.47753 5.36218C5.98721 4.84562 6.29924 4.153 6.29924 3.39123C6.29946 1.79455 4.92872 0.5 3.23796 0.5C1.54737 0.5 0.176692 1.7946 0.176692 3.39123C0.176692 4.44446 0.773389 5.36615 1.66444 5.87157L1.67161 5.87553C1.69709 5.89001 1.723 5.90397 1.74892 5.91751L4.65349 7.50003L7.02802 8.79338L11.7584 11.3704ZM1.46546 3.39137C1.46546 2.46835 2.26046 1.71741 3.23788 1.71741C4.21519 1.71741 5.01029 2.46824 5.01029 3.39137C5.01029 4.31439 4.2153 5.06533 3.23788 5.06533C2.26046 5.06533 1.46546 4.31439 1.46546 3.39137Z"
          fill="black"
          fillOpacity="0.5"
        />
        <path
          d="M6.29923 11.6087C6.29923 10.8468 5.9872 10.1541 5.47752 9.63774L6.32227 9.17762L3.94807 7.88428L1.74903 9.08221C1.72311 9.09596 1.69719 9.10971 1.67171 9.12419L1.66432 9.12815C0.773047 9.63357 0.176351 10.5553 0.176351 11.6087C0.176351 13.2054 1.54709 14.4999 3.23762 14.4999C4.92843 14.4999 6.29917 13.2053 6.29917 11.6087L6.29923 11.6087ZM1.46535 11.6087C1.46535 10.6857 2.26034 9.93473 3.23776 9.93473C4.21507 9.93473 5.01017 10.6856 5.01017 11.6087C5.01017 12.5317 4.21518 13.2827 3.23776 13.2827C2.26068 13.2827 1.46535 12.5315 1.46535 11.6087Z"
          fill="black"
          fillOpacity="0.5"
        />
        <path
          d="M7.73313 5.82299L10.1071 7.11633L15 4.45057C14.683 3.93233 14.1713 3.56128 13.5591 3.4064C12.9469 3.25152 12.3075 3.33099 11.7584 3.63036L7.73313 5.82299Z"
          fill="black"
          fillOpacity="0.5"
        />
      </svg>
    </div>
  );

  return props.isClip ? scissorIcon : null;
}

// Construct the leading part of the pill, consisting of the profile picture and of the title
function PillLeading(props: TawkPillConfig): JSX.Element {
  const { tawkTitle, authorProfilePicConfig } = props;

  const navigate = useNavigate();
  const navigateToAuthorProfile = () => navigate(`/@${props.authorUserHandle}`);

  const titleStyle: React.CSSProperties = {
    fontSize: (() => {
      if (props.size === "small") {
        return 14;
      }
      if (props.size === "normal") {
        return 16;
      }
      if (props.size === "big") {
        return 24;
      }

      assertUnreachable(props.size);
      return 16;
    })(),
    lineHeight: 0.5,
  };

  return (
    <div className="tawk-pill-leading">
      {/* profile picture */}
      <button
        type="button"
        className="tawk-pill-profile-pic-button"
        onClick={navigateToAuthorProfile}
      >
        {GenericImageComponent(authorProfilePicConfig)}
      </button>

      {conditionalScissorIcon(props)}

      {/* tawk title */}
      <div className="tawk-pill-title-container">
        <span className="tawk-pill-title" style={titleStyle}>
          {tawkTitle}
        </span>
      </div>
    </div>
  );
}

function playIcon(config: TawkPillConfig): JSX.Element {
  return (
    <div className="tawk-pill-play-button">
      <svg
        width="11"
        height="13"
        viewBox="0 0 11 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9.5667 5.52992L1.53322 0.466394C0.867316 0.0466717 0 0.525224 0 1.31237V10.8065C0 11.5676 0.816463 12.0497 1.48294 11.6821L9.51642 7.25155C10.183 6.8839 10.2107 5.93585 9.5667 5.52992Z"
          fill={config.colorsProvider.color6TextItself}
        />
      </svg>
    </div>
  );
}

// construct the trailing part of the pill, consisting of the time description and the play icon.
function PillTrailing(props: TawkPillConfig): JSX.Element {
  return (
    <div className="tawk-pill-trailing">
      {playIcon(props)}

      <div className="tawk-pill-duration-description">
        {props.durationDescription}
      </div>
    </div>
  );
}

export function TawkPill(props: TawkPillConfig): JSX.Element {
  const tawkPillStyle: React.CSSProperties = {
    backgroundColor: props.colorsProvider.color2StandardPill,
    height: (() => {
      if (props.size === "small") {
        return 30;
      }
      if (props.size === "normal") {
        return 45;
      }
      if (props.size === "big") {
        return 60;
      }
      assertUnreachable(props.size);
      return 45;
    })(),
    color: props.colorsProvider.color6TextItself,
  };

  return (
    <div className="tawk-pill" style={tawkPillStyle}>
      <PillLeading {...props} />
      <PillTrailing {...props} />
    </div>
  );
}
